// src/views/areasComuns/AreasComumList.jsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import areasConunsService from "../../services/areasConunsService";
import condominiosService from "../../services/condominiosService";
import { useAuth } from "../../contexts/AuthContext";
import AreaComumModal from "./AreaComumModal";
import DeleteModal from "../../components/ui/DeleteModal";

const fmtMoeda = v => Number(v||0).toLocaleString("pt-BR",{style:"currency",currency:"BRL"});
const fmtDt    = dt => { if(!dt) return "-"; try{ return new Date((String(dt||"").split("T")[0])+"T12:00:00").toLocaleDateString("pt-BR"); }catch{return dt;} };
const DIAS_LABEL = {0:"Dom",1:"Seg",2:"Ter",3:"Qua",4:"Qui",5:"Sex",6:"Sáb"};

export default function AreasComumList() {
  const { condominioAtivo, isAdmin, isGerente } = useAuth();
  const { podeEditar } = useAuth();
  const canEdit = podeEditar("areasComuns");
  const [condominios, setCondominios] = useState([]);
  const [idCondo, setIdCondo]         = useState("");
  const [areas, setAreas]             = useState([]);
  const [loading, setLoading]         = useState(false);
  const [areaDetalhe, setAreaDetalhe] = useState(null);
  const [bloqueios, setBloqueios]     = useState([]);

  const [showModal, setShowModal]     = useState(false);
  const [editData, setEditData]       = useState(null);
  const [showDelete, setShowDelete]   = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving]           = useState(false);
  const [deleting, setDeleting]       = useState(false);

  // Bloqueio
  const [showBloqueio, setShowBloqueio] = useState(false);
  const [formBloq, setFormBloq]       = useState({dtInicio:"",dtFim:"",hrInicio:"",hrFim:"",motivo:"Manutenção / Limpeza",bloqueadoPor:"Administrador"});
  const [savingBloq, setSavingBloq]   = useState(false);

  // Sync with auth context when condominioAtivo changes
  // Sync with navbar condominioAtivo — updates whenever user changes it
  useEffect(() => {
    if (condominioAtivo?.id) setIdCondo(String(condominioAtivo.id));
    else setIdCondo("");
  }, [condominioAtivo]);

  useEffect(() => {
    condominiosService.listar({limit:100}).then(r=>{ setCondominios(r.data); }).catch(()=>{});
  }, []);

  const carregar = useCallback(async () => {
    if (!idCondo) return;
    setLoading(true);
    try { const r = await areasConunsService.listar({idCondominio:idCondo}); setAreas(r.data); }
    catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, [idCondo]);

  useEffect(() => { carregar(); }, [carregar]);

  const carregarDetalhe = async (id) => {
    const r = await areasConunsService.buscarPorId(id);
    setAreaDetalhe(r.data);
    setBloqueios(r.data.bloqueios||[]);
  };

  const condoNome = condominios.find(c=>String(c.id)===String(idCondo))?.nmFantasia||"";

  const handleSave = async (form) => {
    setSaving(true);
    try {
      const payload = {...form, idCondominio:idCondo};
      let resultado;
      if(form.id) {
        resultado = await areasConunsService.atualizar(form.id, payload);
      } else {
        resultado = await areasConunsService.criar(payload);
      }
      setShowModal(false);
      await carregar();
      // Atualiza detalhe: só recarrega se estava editando uma área já aberta no painel
      if (form.id && areaDetalhe?.id===form.id) {
        await carregarDetalhe(form.id);
      }
      // Nova área: não abre automaticamente o painel (evita "2 cards")
    } catch(e) { alert(e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await areasConunsService.deletar(deleteTarget.id);
      setShowDelete(false);
      setDeleteTarget(null);
      const novaLista = await areasConunsService.listar({idCondominio:idCondo});
      setAreas(novaLista.data||[]);
      // Se deletou a área que estava no painel, abre a próxima disponível
      if (areaDetalhe?.id===deleteTarget.id) {
        const restantes = (novaLista.data||[]).filter(a=>a.id!==deleteTarget.id);
        if (restantes.length > 0) {
          await carregarDetalhe(restantes[0].id);
        } else {
          setAreaDetalhe(null);
          setBloqueios([]);
        }
      }
    }
    catch(e) { alert(e.message); }
    finally { setDeleting(false); }
  };

  const handleSalvarBloqueio = async () => {
    if(!formBloq.dtInicio||!formBloq.dtFim) return alert("Informe as datas de início e fim.");
    setSavingBloq(true);
    try {
      await areasConunsService.criarBloqueio(areaDetalhe.id, formBloq);
      setShowBloqueio(false); setFormBloq({dtInicio:"",dtFim:"",hrInicio:"",hrFim:"",motivo:"Manutenção / Limpeza",bloqueadoPor:"Administrador"});
      await carregarDetalhe(areaDetalhe.id);
    } catch(e) { alert(e.message); }
    finally { setSavingBloq(false); }
  };

  const handleRemoverBloqueio = async (bid) => {
    if(!window.confirm("Remover bloqueio?")) return;
    await areasConunsService.deletarBloqueio(areaDetalhe.id, bid);
    await carregarDetalhe(areaDetalhe.id);
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title"><i className="bi bi-door-open me-2" style={{color:"var(--primary)",fontSize:22}}/>Áreas Comuns</h1>
          <p className="page-subtitle">Cadastro de áreas disponíveis para reserva</p>
        </div>
        <div className="d-flex gap-2 align-items-center flex-wrap">

          <button className="btn btn-primary" onClick={()=>{ setEditData(null); setShowModal(true); }} disabled={!idCondo || !canEdit}>
            <i className="bi bi-plus-lg"/> Nova Área
          </button>
        </div>
      </div>

      <div style={{display:"flex",gap:16,alignItems:"flex-start"}}>
        {/* Cards de áreas */}
        <div style={{flex:1,display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:16}}>
          {loading ? <div style={{gridColumn:"1/-1",textAlign:"center",padding:"40px 0",color:"var(--gray-400)"}}><span className="spinner-border text-primary"/></div>
          : !idCondo ? <div style={{gridColumn:"1/-1",textAlign:"center",padding:"60px 0",color:"var(--gray-400)"}}><i className="bi bi-buildings" style={{fontSize:40,display:"block",marginBottom:10}}/>Selecione um condomínio</div>
          : areas.length===0 ? <div style={{gridColumn:"1/-1",textAlign:"center",padding:"60px 0",color:"var(--gray-400)"}}><i className="bi bi-door-open" style={{fontSize:40,display:"block",marginBottom:10}}/>Nenhuma área cadastrada</div>
          : areas.map(area=>(
            <div key={area.id} className="card" style={{cursor:"pointer",border:areaDetalhe?.id===area.id?"2px solid var(--primary)":"1px solid var(--gray-200)"}}
              onClick={()=>{ setAreaDetalhe(area); carregarDetalhe(area.id); }}>
              <div style={{padding:"16px",borderBottom:"1px solid var(--gray-100)"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8}}>
                  <div>
                    <div style={{fontSize:15,fontWeight:700,color:"var(--gray-800)"}}>{area.nmArea}</div>
                    <div style={{fontSize:12,color:"var(--gray-400)",marginTop:2}}>{area.tpPeriodo==="DiaInteiro"?"Dia Inteiro":`${area.hrInicio?.substring(0,5)||""}–${area.hrFim?.substring(0,5)||""}`}</div>
                  </div>
                  <span style={{background:area.situacao==="Ativa"?"#DCFCE7":"#FEE2E2",color:area.situacao==="Ativa"?"#166534":"#991B1B",padding:"2px 8px",borderRadius:12,fontSize:11,fontWeight:600,flexShrink:0}}>{area.situacao==="Ativa"?"Ativa":"Inativa"}</span>
                </div>
                {area.capacidade&&<div style={{fontSize:12,color:"var(--gray-500)",marginTop:4}}><i className="bi bi-people me-1"/>{area.capacidade} pessoas</div>}
              </div>
              <div style={{padding:"12px 16px",display:"flex",gap:16,flexWrap:"wrap"}}>
                <div><div style={{fontSize:11,color:"var(--gray-400)"}}>Locação</div><div style={{fontSize:13,fontWeight:700,color:"var(--primary)"}}>{fmtMoeda(area.vlrLocacao)}</div></div>
                {parseFloat(area.vlrCaucao)>0&&<div><div style={{fontSize:11,color:"var(--gray-400)"}}>Caução</div><div style={{fontSize:13,fontWeight:700,color:"#854D0E"}}>{fmtMoeda(area.vlrCaucao)}</div></div>}
                <div><div style={{fontSize:11,color:"var(--gray-400)"}}>Reservas</div><div style={{fontSize:13,fontWeight:700}}>{area.qtdReservas||0}</div></div>
                {area.qtdBloqueios>0&&<div><div style={{fontSize:11,color:"var(--gray-400)"}}>Bloqueios</div><div style={{fontSize:13,fontWeight:700,color:"#991B1B"}}>{area.qtdBloqueios}</div></div>}
              </div>
              <div style={{padding:"8px 16px",borderTop:"1px solid var(--gray-100)",display:"flex",gap:6}}>
                <button className="btn btn-outline-secondary btn-sm" style={{fontSize:11}} onClick={e=>{ e.stopPropagation(); setEditData(area); setShowModal(true); }} disabled={!canEdit}><i className="bi bi-pencil me-1"/>Editar</button>
                <button className="btn btn-outline-danger btn-sm" style={{fontSize:11}} onClick={e=>{ e.stopPropagation(); setDeleteTarget(area); setShowDelete(true); }} disabled={!canEdit}><i className="bi bi-trash3"/></button>
              </div>
            </div>
          ))}
        </div>

        {/* Painel de detalhe / bloqueios */}
        {areaDetalhe && (
          <div style={{width:340,flexShrink:0}}>
            <div className="card">
              <div style={{padding:"14px 16px",borderBottom:"1px solid var(--gray-100)",background:"var(--primary)",borderRadius:"12px 12px 0 0"}}>
                <div style={{color:"#fff",fontWeight:700,fontSize:14}}>{areaDetalhe.nmArea}</div>
                <div style={{color:"rgba(255,255,255,.7)",fontSize:12,marginTop:2}}>
                  {areaDetalhe.tpPeriodo==="DiaInteiro"?"Dia Inteiro":`${areaDetalhe.hrInicio?.substring(0,5)}–${areaDetalhe.hrFim?.substring(0,5)}`}
                  {areaDetalhe.capacidade&&` • ${areaDetalhe.capacidade} pessoas`}
                </div>
              </div>

              {/* Dias permitidos */}
              <div style={{padding:"12px 16px",borderBottom:"1px solid var(--gray-100)"}}>
                <div style={{fontSize:11,fontWeight:700,color:"var(--gray-500)",textTransform:"uppercase",letterSpacing:".5px",marginBottom:6}}>Dias Disponíveis</div>
                <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                  {[0,1,2,3,4,5,6].map(d=>{
                    const dias = (areaDetalhe.diasPermitidos||"").split(",");
                    const ativo = dias.includes(String(d));
                    return <span key={d} style={{padding:"2px 8px",borderRadius:12,fontSize:11,fontWeight:600,background:ativo?"var(--primary)":"var(--gray-100)",color:ativo?"#fff":"var(--gray-400)"}}>{DIAS_LABEL[d]}</span>;
                  })}
                </div>
              </div>

              {/* Limites */}
              <div style={{padding:"12px 16px",borderBottom:"1px solid var(--gray-100)",display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                <div><div style={{fontSize:11,color:"var(--gray-400)"}}>Antecedência mín.</div><div style={{fontSize:13,fontWeight:600}}>{areaDetalhe.diasAntecedencia} dia(s)</div></div>
                <div><div style={{fontSize:11,color:"var(--gray-400)"}}>Antecedência máx.</div><div style={{fontSize:13,fontWeight:600}}>{areaDetalhe.diasMaxAntecedencia} dias</div></div>
                <div><div style={{fontSize:11,color:"var(--gray-400)"}}>Limite/mês p/ unidade</div><div style={{fontSize:13,fontWeight:600}}>{areaDetalhe.limiteReservasMes||"Sem limite"}</div></div>
                <div><div style={{fontSize:11,color:"var(--gray-400)"}}>Cancelamento</div><div style={{fontSize:13,fontWeight:600,color:"#C2410C"}}>{fmtMoeda(areaDetalhe.vlrTaxaCancelamento)}</div></div>
              </div>

              {/* Bloqueios */}
              <div style={{padding:"12px 16px"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                  <div style={{fontSize:12,fontWeight:700,color:"var(--gray-700)"}}>Bloqueios de Datas</div>
                  <button className="btn btn-outline-danger btn-sm" style={{fontSize:11}} onClick={()=>setShowBloqueio(true)}>
                    <i className="bi bi-lock me-1"/>Bloquear
                  </button>
                </div>

                {/* Form novo bloqueio */}
                {showBloqueio&&(
                  <div style={{background:"#FFF7ED",border:"1px solid #FED7AA",borderRadius:8,padding:12,marginBottom:12}}>
                    <div style={{fontSize:12,fontWeight:700,color:"#C2410C",marginBottom:8}}><i className="bi bi-lock me-1"/>Novo Bloqueio</div>
                    <div className="row g-2">
                      <div className="col-6"><label style={{fontSize:11,fontWeight:600,color:"var(--gray-600)"}}>De</label><input type="date" className="form-control form-control-sm" value={formBloq.dtInicio} onChange={e=>setFormBloq(p=>({...p,dtInicio:e.target.value}))}/></div>
                      <div className="col-6"><label style={{fontSize:11,fontWeight:600,color:"var(--gray-600)"}}>Até</label><input type="date" className="form-control form-control-sm" value={formBloq.dtFim} onChange={e=>setFormBloq(p=>({...p,dtFim:e.target.value}))}/></div>
                      <div className="col-12"><label style={{fontSize:11,fontWeight:600,color:"var(--gray-600)"}}>Motivo</label><input className="form-control form-control-sm" value={formBloq.motivo} onChange={e=>setFormBloq(p=>({...p,motivo:e.target.value}))}/></div>
                      <div className="col-12"><label style={{fontSize:11,fontWeight:600,color:"var(--gray-600)"}}>Bloqueado por</label><input className="form-control form-control-sm" value={formBloq.bloqueadoPor} onChange={e=>setFormBloq(p=>({...p,bloqueadoPor:e.target.value}))}/></div>
                      <div className="col-12 d-flex gap-2">
                        <button className="btn btn-danger btn-sm flex-fill" onClick={handleSalvarBloqueio} disabled={savingBloq}>{savingBloq?<span className="spinner-border spinner-border-sm"/>:"Confirmar"}</button>
                        <button className="btn btn-outline-secondary btn-sm" onClick={()=>setShowBloqueio(false)}><i className="bi bi-x"/></button>
                      </div>
                    </div>
                  </div>
                )}

                {bloqueios.length===0
                  ? <p style={{fontSize:12.5,color:"var(--gray-400)",textAlign:"center",padding:"12px 0"}}>Sem bloqueios ativos</p>
                  : <div style={{display:"flex",flexDirection:"column",gap:6}}>
                      {bloqueios.map(b=>(
                        <div key={b.id} style={{background:"#FFF7ED",border:"1px solid #FED7AA",borderRadius:6,padding:"8px 10px",display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:6}}>
                          <div>
                            <div style={{fontSize:12,fontWeight:600,color:"#C2410C"}}>{fmtDt(b.dtInicio)} → {fmtDt(b.dtFim)}</div>
                            <div style={{fontSize:11,color:"#C2410C",opacity:.8}}>{b.motivo}</div>
                            {b.bloqueadoPor&&<div style={{fontSize:10.5,color:"var(--gray-400)"}}>{b.bloqueadoPor}</div>}
                          </div>
                          <button className="btn btn-sm" style={{padding:"2px 6px",fontSize:11,color:"#991B1B",border:"none",background:"transparent"}} onClick={()=>handleRemoverBloqueio(b.id)} title="Remover bloqueio">
                            <i className="bi bi-x-circle"/>
                          </button>
                        </div>
                      ))}
                    </div>
                }
              </div>
            </div>
          </div>
        )}
      </div>

      <AreaComumModal show={showModal} onClose={()=>setShowModal(false)} onSave={handleSave}
        editData={editData} saving={saving} idCondominio={idCondo} condominioNome={condoNome}/>
      <DeleteModal show={showDelete} onClose={()=>setShowDelete(false)} onConfirm={handleDelete}
        itemName={deleteTarget?.nmArea} loading={deleting}/>
    </div>
  );
}
